/**
 * Open tabs (OneInvoice style "Dashboard | Sale Invoice (AR) × | INV-2026-08 ×").
 *
 * Two kinds of tab:
 *  - module tabs: one per sidebar module (list screens). Navigating inside the module
 *    (filters, pagination) keeps the same tab and remembers the last path.
 *  - record tabs: declared via a module's `subTabs` rules in moduleRegistry
 *    (New / View / Edit / Structure …). Each record gets its own tab, inserted right
 *    after its parent module tab. Pages refine the caption with `useTabLabel()`.
 *
 * Dashboard tab is pinned and cannot be closed. Tabs persist per user.
 */
import React, { createContext, useContext, useEffect, useMemo, useReducer, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { resolveTab, getHomePath, getAllModules } from './moduleRegistry';

const TabsContext = createContext(null);

const storageKey = (user) => `lms_tabs_${user?.role || 'anon'}_${user?.id || ''}`;

function load(user) {
  try {
    const raw = localStorage.getItem(storageKey(user));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed.tabs)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function save(user, state) {
  try {
    localStorage.setItem(storageKey(user), JSON.stringify({ tabs: state.tabs, activeKey: state.activeKey }));
  } catch {
    /* storage unavailable — tabs just won't persist */
  }
}

function insertAfterFamily(tabs, parentKey, tab) {
  // Place a record tab after its module tab and after that module's existing record tabs.
  let idx = tabs.findIndex((t) => t.key === parentKey);
  if (idx === -1) return [...tabs, tab];
  while (idx + 1 < tabs.length && tabs[idx + 1].parentKey === parentKey) idx += 1;
  return [...tabs.slice(0, idx + 1), tab, ...tabs.slice(idx + 1)];
}

function reducer(state, action) {
  switch (action.type) {
    case 'hydrate':
      return { ...state, ...action.state };
    case 'visit': {
      const { tab, path, pinnedModule } = action;
      let tabs = state.tabs;
      // the pinned (Dashboard) tab always exists and always comes first
      if (pinnedModule && !tabs.some((t) => t.key === pinnedModule.key)) {
        tabs = [{ key: pinnedModule.key, label: pinnedModule.label, path: pinnedModule.path, pinned: true, parentKey: null }, ...tabs];
      }
      // a record tab needs its parent module tab open too
      if (tab.parentKey && !tabs.some((t) => t.key === tab.parentKey)) {
        const parent = action.parentModule;
        if (parent) tabs = [...tabs, { key: parent.key, label: parent.label, path: parent.path, pinned: false, parentKey: null }];
      }
      const exists = tabs.find((t) => t.key === tab.key);
      if (exists) {
        tabs = tabs.map((t) => (t.key === tab.key ? { ...t, path } : t));
      } else {
        const fresh = { key: tab.key, label: tab.label, path, pinned: tab.pinned, parentKey: tab.parentKey || null, isRecord: !!tab.isRecord };
        tabs = tab.parentKey ? insertAfterFamily(tabs, tab.parentKey, fresh) : [...tabs, fresh];
      }
      return { tabs, activeKey: tab.key };
    }
    case 'rename': {
      if (!state.tabs.some((t) => t.key === action.key && t.label !== action.label)) return state;
      return { ...state, tabs: state.tabs.map((t) => (t.key === action.key ? { ...t, label: action.label } : t)) };
    }
    case 'close': {
      const idx = state.tabs.findIndex((t) => t.key === action.key);
      if (idx === -1 || state.tabs[idx].pinned) return state;
      // closing a module tab also closes its record tabs
      const victims = new Set([action.key, ...state.tabs.filter((t) => t.parentKey === action.key).map((t) => t.key)]);
      const tabs = state.tabs.filter((t) => !victims.has(t.key));
      let activeKey = state.activeKey;
      if (victims.has(activeKey)) {
        const neighbour = tabs[idx - 1] || tabs[idx] || tabs[0];
        activeKey = neighbour ? neighbour.key : null;
      }
      return { tabs, activeKey };
    }
    case 'closeOthers': {
      const keep = state.tabs.find((t) => t.key === action.key);
      const tabs = state.tabs.filter((t) => t.pinned || t.key === action.key || (keep?.parentKey && t.key === keep.parentKey));
      return { tabs, activeKey: action.key };
    }
    case 'closeAll': {
      const tabs = state.tabs.filter((t) => t.pinned);
      return { tabs, activeKey: tabs[0]?.key || null };
    }
    default:
      return state;
  }
}

export function TabsProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, { tabs: [], activeKey: null });

  // Hydrate once per user
  useEffect(() => {
    if (!user) return;
    const stored = load(user);
    if (stored) dispatch({ type: 'hydrate', state: stored });
  }, [user?.id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync route → tabs
  useEffect(() => {
    if (!user) return;
    const tab = resolveTab(location.pathname, user.role);
    if (!tab) return;
    const modules = getAllModules(user.role);
    const path = location.pathname + location.search;
    dispatch({
      type: 'visit',
      tab,
      path,
      pinnedModule: modules.find((m) => m.pinned),
      parentModule: tab.parentKey ? modules.find((m) => m.key === tab.parentKey) : null,
    });
  }, [location.pathname, location.search, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist
  useEffect(() => {
    if (user && state.tabs.length) save(user, state);
  }, [state, user]);

  const iconsByKey = useMemo(() => {
    const map = {};
    getAllModules(user?.role).forEach((m) => {
      map[m.key] = m.icon;
      (m.subTabs || []).forEach((r) => { if (r.icon) map[`__rule:${r.pattern}`] = r.icon; });
    });
    return map;
  }, [user?.role]);

  const iconFor = useCallback((tab) => {
    if (!tab.isRecord) return iconsByKey[tab.key];
    // derive from the rule that produced the tab's current path
    const resolved = resolveTab(tab.path.split('?')[0], user?.role);
    return resolved?.icon || iconsByKey[tab.parentKey];
  }, [iconsByKey, user?.role]);

  const activate = useCallback((key) => {
    const tab = state.tabs.find((t) => t.key === key);
    if (tab) navigate(tab.path);
  }, [state.tabs, navigate]);

  const close = useCallback((key) => {
    const idx = state.tabs.findIndex((t) => t.key === key);
    if (idx === -1 || state.tabs[idx].pinned) return;
    const victims = new Set([key, ...state.tabs.filter((t) => t.parentKey === key).map((t) => t.key)]);
    const remaining = state.tabs.filter((t) => !victims.has(t.key));
    dispatch({ type: 'close', key });
    if (victims.has(state.activeKey)) {
      const neighbour = remaining[idx - 1] || remaining[idx] || remaining[0];
      navigate(neighbour ? neighbour.path : getHomePath(user?.role));
    }
  }, [state.tabs, state.activeKey, navigate, user?.role]);

  /** Close the current tab and go to `path` (e.g. after "New" saves → open Edit). */
  const closeAndNavigate = useCallback((key, path) => {
    dispatch({ type: 'close', key });
    navigate(path, { replace: true });
  }, [navigate]);

  const closeOthers = useCallback((key) => {
    dispatch({ type: 'closeOthers', key });
    const tab = state.tabs.find((t) => t.key === key);
    if (tab && state.activeKey !== key) navigate(tab.path);
  }, [state.tabs, state.activeKey, navigate]);

  const closeAll = useCallback(() => {
    dispatch({ type: 'closeAll' });
    navigate(getHomePath(user?.role));
  }, [navigate, user?.role]);

  const rename = useCallback((key, label) => {
    if (key && label) dispatch({ type: 'rename', key, label });
  }, []);

  const value = useMemo(() => ({
    tabs: state.tabs.map((t) => ({ ...t, icon: iconFor(t) })),
    activeKey: state.activeKey,
    activate,
    close,
    closeAndNavigate,
    closeOthers,
    closeAll,
    rename,
    /** open a route in its own tab — identical to navigate(); exists for readability at call sites */
    open: (path) => navigate(path),
  }), [state, iconFor, activate, close, closeAndNavigate, closeOthers, closeAll, rename, navigate]);

  return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
}

export function useTabs() {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('useTabs must be used within TabsProvider');
  return ctx;
}

/**
 * Give the *current* tab a data-driven caption once the record loads, e.g.
 *   useTabLabel(course && `Edit: ${course.title}`)
 */
export function useTabLabel(label) {
  const { activeKey, rename } = useTabs();
  const location = useLocation();
  const { user } = useAuth();
  useEffect(() => {
    if (!label) return;
    const tab = resolveTab(location.pathname, user?.role);
    const key = tab?.key || activeKey;
    if (key) rename(key, label);
  }, [label, activeKey, location.pathname, user?.role, rename]);
}
